import React from 'react';
import {ToasterWithStatusAndDesriptionParams} from '../../context-providers/ToasterProvider/ToasterProvider';
import {contractAddresses} from '../../helpers/config/contracts.config';
import {getErrorMessage} from '../../helpers/error-message.helper';
import Singleton from '../../helpers/singleton';
import {BigNumber} from 'ethers';

interface InjectedDependencies {
    publicClient: any;
    nrsContract: any;
    lockNrsContract: any,
    nrsLockWithdrawalTimelockContract: any,
    userAddress: `0x${string}` | undefined;
    addToasterWithStatusAndDescription: (params: ToasterWithStatusAndDesriptionParams) => void;
    removeToaster: (id: string) => void;
    closeModal: () => void;
    signTypedData: (data: any) => Promise<string>;
}

@Singleton
export class DepositWithdrawalLockService {

    private publicClient!: any;
    private nrsContract!: any;
    private lockNrsContract!: any;
    private nrsLockWithdrawalTimelockContract!: any;
    private userAddress?: string;
    private showToaster!: (params: ToasterWithStatusAndDesriptionParams) => void;
    private removeToaster!: (id: string) => void;
    private closeModal!: () => void;

    public injectDependencies(deps: InjectedDependencies) {
        this.publicClient = deps.publicClient;
        this.nrsContract = deps.nrsContract;
        this.userAddress = deps.userAddress;
        this.lockNrsContract = deps.lockNrsContract;
        this.nrsLockWithdrawalTimelockContract = deps.nrsLockWithdrawalTimelockContract;
        this.showToaster = deps.addToasterWithStatusAndDescription;
        this.removeToaster = deps.removeToaster;
        this.closeModal = deps.closeModal;
    }

    public async withdraw(amountBN: BigNumber) {
        const toasterId = this.getToasterId('withdraw', amountBN.toString());
        if (!this.userAddress) {
            throw new Error('User address or signer is not defined');
        }

        const withdrawPendingDescription = this.getWithdrawToasterDescription();

        this.showToaster({
            id: toasterId,
            status: 'pending',
            title: 'Withdraw NRS',
            description: withdrawPendingDescription
        });
        try {
            const hash = await this.lockNrsContract.write.withdraw([amountBN, this.userAddress, this.userAddress]);
            this.closeModal();
            await this.publicClient.waitForTransactionReceipt({ hash })

            this.removeToaster(toasterId);
            this.showToaster({
                id: toasterId,
                status: 'success',
                title: 'Withdraw NRS',
                description: withdrawPendingDescription,
                closeTime: 10000,
            })
        } catch (err: any) {
            this.showToaster({
                id: toasterId,
                status: 'error',
                title: 'Withdraw NRS',
                description: getErrorMessage(err),
                closeTime: 15000
            });
            throw err;
        }
    }

    public async claim(indices: number[]) {
        const toasterId = this.getToasterId('claim', Date.now().toString());
        if (!this.userAddress) {
            throw new Error('User address or signer is not defined');
        }

        this.showToaster({
            id: toasterId,
            status: 'pending',
            title: 'Claim NRS',
            description: ''
        });
        try {
            const sortedDescIndices = [...indices].sort((a, b) => b - a);
            const hash = await this.nrsLockWithdrawalTimelockContract.write.claim([sortedDescIndices]);
            this.closeModal();
            await this.publicClient.waitForTransactionReceipt({ hash })

            this.removeToaster(toasterId);
            this.showToaster({
                id: toasterId,
                status: 'success',
                title: 'Claim NRS',
                description: '',
                closeTime: 10000,
            })
        } catch (err: any) {
            console.log('err', err)
            this.showToaster({
                id: toasterId,
                status: 'error',
                title: 'Claim NRS',
                description: getErrorMessage(err),
                closeTime: 15000
            });
            throw err;
        }
    }

    public async approveAmount(amount: BigNumber) {
        const toasterId = this.getToasterId('approve', amount.toString());
        const approveDescription = 'Approve Nereus to use NRS for making deposits.';
        this.showToaster({
            id: toasterId,
            status: 'pending',
            title: 'Approve NRS',
            description: approveDescription
        });
        try {
            const hash = await this.nrsContract.write.approve([contractAddresses.base.lockNrs, amount]);
            await this.publicClient.waitForTransactionReceipt({ hash })

            this.showToaster({
                id: toasterId,
                status: 'success',
                title: 'Approve NRS',
                description: approveDescription,
                closeTime: 10000,
            })
        } catch (error) {
            this.showToaster({
                id: toasterId,
                status: 'error',
                title: 'Approve NRS',
                description: getErrorMessage(error),
                closeTime: 10000,
            });
            throw error;
        }
    }

    public async deposit(amountBN: BigNumber) {
        if (!this.userAddress) {
            throw new Error('User address or signer is not defined');
        }
        const toasterId = this.getToasterId('deposit', amountBN.toString());
        const title = 'Locking NRS';
        this.showToaster({
            id: toasterId,
            status: 'pending',
            title,
            description: this.getDepositToasterDescription()
        });
        try {
            const hash = await this.lockNrsContract.write.deposit([amountBN, this.userAddress]);
            this.closeModal();
            await this.publicClient.waitForTransactionReceipt({ hash })

            this.removeToaster(toasterId);
            this.showToaster({
                id: toasterId,
                status: 'success',
                title: 'Lock NRS',
                description: this.getDepositToasterDescription(),
                closeTime: 10000,
            })
        } catch (error) {
            this.showToaster({
                id: toasterId,
                status: 'error',
                title,
                description: getErrorMessage(error),
                closeTime: 10000,
            });
            throw error;
        }
    }

    private getToasterId(actionType: string, amount: string) {
        return JSON.stringify({
            actionType,
            amount,
            timestamp: Date.now()
        });
    }

    public getWithdrawToasterDescription() {
        return <span className="nereus-color-main">Withdrawals will be available for claiming after 3-month redemption period.</span>
    }

    public getDepositToasterDescription() {
        return <>Your funds will be available on your balance shortly.</>
    }
}